const CAB_BUILDER_VERSION = '24.1';

if (!customElements.get('cab-builder-v24')) {
  customElements.define(
    'cab-builder-v24',
    class MinifigureBuilder extends HTMLElement {
      constructor() {
        super();
        this.config = JSON.parse(this.querySelector('[data-cab-config]').textContent);
        this.selections = {
          hairHeadgear: null,
          head: null,
          torso: null,
          legs: null,
        };
        this.queries = {
          hairHeadgear: '',
          head: '',
          torso: '',
          legs: '',
        };
        this.activeColors = {
          hairHeadgear: '',
          head: '',
          torso: '',
          legs: '',
        };
        this.visibleCounts = {
          hairHeadgear: this.config.productsPerBatch,
          head: this.config.productsPerBatch,
          torso: this.config.productsPerBatch,
          legs: this.config.productsPerBatch,
        };
        this.products = new Map();
        this.loading = new Map();
        this.activeFilters = new Map();
        this.currentHairCategory = this.config.defaultHairCategory || 'hair';
        this.currentSlot = null;
        this.filterControlsBuilt = false;
        this.searchTimer = null;
      }

      connectedCallback() {
        this.dataset.builderVersion = CAB_BUILDER_VERSION;
        console.info(`[CAB Builder] v${CAB_BUILDER_VERSION} loaded`);
        this.cacheElements();
        this.bindEvents();
        this.updateSummary();

        const deepLinkRequests = this.getDeepLinkRequests();
        const requestedHairHeadgear = deepLinkRequests.find((request) => request.slot === 'hairHeadgear');
        if (requestedHairHeadgear) this.activateHairCategory(requestedHairHeadgear.collectionKey);

        this.openSlot('hairHeadgear', false);
        if (deepLinkRequests.length) void this.applyDeepLinkSelections(deepLinkRequests);
        this.emitAnalytics('cab_builder_view', { entry_source: this.getEntrySource() });
      }

      getDeepLinkRequests() {
        const params = new URLSearchParams(window.location.search);
        const definitions = this.config.deepLinks || [
          { parameter: 'hair_variant', slot: 'hairHeadgear', collectionKey: 'hair' },
          { parameter: 'headgear_variant', slot: 'hairHeadgear', collectionKey: 'headgear' },
          { parameter: 'head_variant', slot: 'head', collectionKey: 'head' },
          { parameter: 'torso_variant', slot: 'torso', collectionKey: 'torso' },
          { parameter: 'legs_variant', slot: 'legs', collectionKey: 'legs' },
        ];
        const requestedSlots = new Set();

        return definitions.flatMap((definition) => {
          const variantId = params.get(definition.parameter)?.trim();
          if (!variantId || requestedSlots.has(definition.slot)) return [];
          requestedSlots.add(definition.slot);
          return [{ ...definition, variantId }];
        });
      }

      async applyDeepLinkSelections(requests) {
        await Promise.all(requests.map((request) => this.loadSlot(request.slot)));

        const selectedParts = [];
        requests.forEach((request) => {
          const product = (this.products.get(request.collectionKey) || []).find((candidate) =>
            candidate.variants.some((variant) => String(variant.id) === request.variantId)
          );
          const variant = product?.variants.find((candidate) => String(candidate.id) === request.variantId);
          if (!product || !variant) return;

          this.applySelection(request.slot, request.collectionKey, product, variant, {
            advance: false,
            source: 'deep_link',
          });
          selectedParts.push(request.collectionKey);
        });

        this.emitAnalytics('cab_builder_deep_link', {
          requested_parts: requests.map((request) => request.collectionKey).join(','),
          selected_parts: selectedParts.join(','),
        });
      }

      cacheElements() {
        this.panels = new Map(
          Array.from(this.querySelectorAll('[data-slot-panel]')).map((panel) => [panel.dataset.slotPanel, panel])
        );
        this.summaryCards = new Map(
          Array.from(this.querySelectorAll('[data-summary-card]')).map((card) => [card.dataset.summaryCard, card])
        );
        this.totalElement = this.querySelector('[data-total-price]');
        this.completionElement = this.querySelector('[data-completion-message]');
        this.addButton = this.querySelector('[data-add-build]');
        this.addLabel = this.querySelector('[data-add-label]');
        this.messageElement = this.querySelector('[data-builder-message]');
        this.filterDialog = this.querySelector('[data-filter-dialog]');
      }

      bindEvents() {
        this.addEventListener('click', (event) => this.handleClick(event));
        this.addEventListener('change', (event) => this.handleChange(event));
        this.addEventListener('input', (event) => this.handleInput(event));

        if (this.filterDialog) {
          this.filterDialog.addEventListener('click', (event) => {
            if (event.target === this.filterDialog) this.filterDialog.close();
          });
        }
      }

      handleClick(event) {
        const openSlotButton = event.target.closest('[data-open-slot]');
        if (openSlotButton) {
          this.openSlot(openSlotButton.dataset.openSlot);
          return;
        }

        const removeButton = event.target.closest('[data-remove-slot]');
        if (removeButton) {
          this.removeSelection(removeButton.dataset.removeSlot);
          return;
        }

        const hairCategoryButton = event.target.closest('[data-hair-category]');
        if (hairCategoryButton) {
          this.setHairCategory(hairCategoryButton.dataset.hairCategory);
          return;
        }

        if (event.target.closest('[data-no-hair]')) {
          this.removeSelection('hairHeadgear');
          this.openSlot('head');
          return;
        }

        const selectButton = event.target.closest('[data-select-product]');
        if (selectButton) {
          this.selectProduct(selectButton);
          return;
        }

        const showMoreButton = event.target.closest('[data-show-more]');
        if (showMoreButton) {
          const slot = showMoreButton.dataset.showMore;
          this.visibleCounts[slot] += this.config.productsPerBatch;
          this.renderSlot(slot);
          return;
        }

        const retryButton = event.target.closest('[data-retry-slot]');
        if (retryButton) {
          this.loadSlot(retryButton.dataset.retrySlot, true);
          return;
        }

        if (event.target.closest('[data-open-all-filters]')) {
          this.filterDialog?.showModal();
          return;
        }

        if (event.target.closest('[data-close-all-filters]') || event.target.closest('[data-dialog-show-results]')) {
          this.filterDialog?.close();
          return;
        }

        if (
          event.target.closest('[data-clear-filters]') ||
          event.target.closest('[data-dialog-clear-filters]')
        ) {
          this.clearFilters();
          return;
        }

        const activeFilterButton = event.target.closest('[data-remove-filter]');
        if (activeFilterButton) {
          this.removeFilter(activeFilterButton.dataset.filterKey, activeFilterButton.dataset.filterValue);
          return;
        }

        if (event.target.closest('[data-add-build]')) this.addBuildToCart();
      }

      handleChange(event) {
        const colorSelect = event.target.closest('[data-color-filter-select]');
        if (colorSelect) {
          this.setColorFilter(colorSelect.dataset.colorFilterSelect, colorSelect.value);
          return;
        }

        const colorInput = event.target.closest('[data-color-filter-input]');
        if (colorInput) {
          this.setColorFilter(colorInput.dataset.colorFilterInput, colorInput.value);
          return;
        }

        const filterInput = event.target.closest('[data-filter-input]');
        if (filterInput) {
          this.setFilter(filterInput.dataset.filterKey, filterInput.value, filterInput.checked);
          return;
        }

        const variantSelect = event.target.closest('[data-variant-select]');
        if (variantSelect) {
          this.handleVariantChange(variantSelect);
        }
      }

      handleInput(event) {
        const input = event.target.closest('[data-search-input]');
        if (!input) return;

        const slot = input.dataset.searchInput;
        this.queries[slot] = input.value;
        this.visibleCounts[slot] = this.config.productsPerBatch;
        window.clearTimeout(this.searchTimer);
        this.searchTimer = window.setTimeout(() => {
          this.renderSlot(slot);
          if (slot === 'head') this.syncFilterControls();
          this.emitAnalytics('cab_builder_search', {
            part: slot,
            search_term: input.value.trim(),
          });
        }, 180);
      }

      async openSlot(slot, moveFocus = true) {
        if (!this.panels.has(slot)) return;
        this.currentSlot = slot;

        this.panels.forEach((panel, panelSlot) => {
          panel.hidden = panelSlot !== slot;
        });
        this.summaryCards.forEach((card, cardSlot) => {
          card.classList.toggle('cab-summary-card--active', cardSlot === slot);
          card.querySelector('[data-open-slot]')?.setAttribute('aria-expanded', cardSlot === slot ? 'true' : 'false');
        });

        const panel = this.panels.get(slot);
        if (moveFocus) {
          panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
          panel.querySelector('h2')?.focus({ preventScroll: true });
        }

        await this.loadSlot(slot);
      }

      collectionKeyForSlot(slot) {
        if (slot === 'hairHeadgear') return this.currentHairCategory;
        return slot;
      }

      async loadSlot(slot, force = false) {
        const collectionKey = this.collectionKeyForSlot(slot);
        const handle = this.config.collections[collectionKey];
        if (!handle) return;

        if (force) {
          this.products.delete(collectionKey);
          this.loading.delete(collectionKey);
        }

        if (this.products.has(collectionKey)) {
          this.renderSlot(slot);
          if (slot === 'head' && !this.filterControlsBuilt) this.renderFilterControls();
          return;
        }

        if (this.loading.has(collectionKey)) {
          await this.loading.get(collectionKey);
          this.renderSlot(slot);
          return;
        }

        this.renderLoading(slot);
        const promise = this.fetchCollection(handle, slot)
          .then((products) => {
            products.forEach((product, index) => {
              product._collectionOrder = index;
              product._search = this.buildSearchRecord(product);
            });
            this.products.set(collectionKey, products);
            if (slot === 'head') this.renderFilterControls();
            this.renderSlot(slot);
          })
          .catch((error) => {
            console.error(`${this.config.builderType || 'minifigure'} builder collection load failed`, error);
            this.renderLoadError(slot);
          })
          .finally(() => this.loading.delete(collectionKey));

        this.loading.set(collectionKey, promise);
        await promise;
      }

      async fetchCollection(handle, slot) {
        const firstPage = await this.fetchCollectionPage(handle, 1);
        const pages = Number(firstPage.collection.pages || 1);
        const allProducts = [...this.normaliseRecords(firstPage.products, 'product')];

        if (pages > 1) {
          this.renderLoading(slot, allProducts.length, firstPage.collection.productCount);
          const requests = [];
          for (let page = 2; page <= pages; page += 1) requests.push(this.fetchCollectionPage(handle, page));
          const laterPages = await Promise.all(requests);
          laterPages.forEach((payload) => allProducts.push(...this.normaliseRecords(payload.products, 'product')));
        }

        return allProducts
          .map((product) => ({
            ...product,
            variants: this.normaliseRecords(product.variants, 'variant'),
          }))
          .filter((product) => product.variants.length > 0);
      }

      normaliseRecords(records, kind) {
        if (records === null || records === undefined) return [];

        if (typeof records === 'string') {
          try {
            return this.normaliseRecords(JSON.parse(records), kind);
          } catch (_error) {
            return [];
          }
        }

        if (Array.isArray(records)) {
          return records.flatMap((record) => this.normaliseRecords(record, kind));
        }

        if (typeof records !== 'object') return [];

        const isProduct = kind === 'product' && ('variants' in records || ('handle' in records && 'title' in records));
        const isVariant = kind === 'variant' && 'id' in records && ('price' in records || 'formattedPrice' in records);
        if (isProduct || isVariant) return [records];

        const containerKeys = kind === 'product'
          ? ['products', 'items', 'nodes', 'edges', 'results', 'data', 'node']
          : ['variants', 'items', 'nodes', 'edges', 'data', 'node'];
        for (const key of containerKeys) {
          if (key in records) return this.normaliseRecords(records[key], kind);
        }

        return Object.values(records).flatMap((record) => this.normaliseRecords(record, kind));
      }

      async fetchCollectionPage(handle, page) {
        const root = this.config.rootUrl.endsWith('/') ? this.config.rootUrl : `${this.config.rootUrl}/`;
        const url = new URL(`${root}collections/${handle}`, window.location.origin);
        url.searchParams.set('view', 'builder-data-v24');
        url.searchParams.set('cab_builder_version', CAB_BUILDER_VERSION);
        url.searchParams.set('page', String(page));
        const previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');
        if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);

        const response = await fetch(url.toString(), {
          headers: { Accept: 'text/html' },
          credentials: 'same-origin',
          cache: 'no-store',
        });
        if (!response.ok) throw new Error(`Collection request failed with ${response.status}`);
        const body = await response.text();
        const payload = JSON.parse(body);
        if (payload.builderDataVersion !== CAB_BUILDER_VERSION) {
          throw new Error(`Builder data version mismatch: expected ${CAB_BUILDER_VERSION}, got ${payload.builderDataVersion || 'none'}`);
        }
        return payload;
      }

      renderLoading(slot, loaded = 0, total = 0) {
        const browser = this.getBrowser(slot);
        browser.replaceChildren();
        const status = document.createElement('p');
        status.className = 'cab-browser-status';
        status.setAttribute('role', 'status');
        status.textContent = loaded
          ? this.interpolate(this.config.strings.loadingProgress, { loaded, total })
          : this.config.strings.loading;
        browser.append(status);
      }

      renderLoadError(slot) {
        const browser = this.getBrowser(slot);
        browser.replaceChildren();
        const error = document.createElement('div');
        error.className = 'cab-browser-error';
        error.setAttribute('role', 'alert');
        const message = document.createElement('p');
        message.textContent = this.config.strings.loadError;
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'button button--secondary';
        retry.dataset.retrySlot = slot;
        retry.textContent = this.config.strings.retry;
        error.append(message, retry);
        browser.append(error);
      }

      getBrowser(slot) {
        return this.panels.get(slot).querySelector(`[data-browser="${slot}"]`);
      }

      renderSlot(slot) {
        const collectionKey = this.collectionKeyForSlot(slot);
        if (!this.products.has(collectionKey)) return;
        const browser = this.getBrowser(slot);
        this.renderColorFilter(slot);
        const results = this.getResults(slot);
        const visibleCount = Math.min(this.visibleCounts[slot], results.length);
        browser.replaceChildren();

        const status = document.createElement('div');
        status.className = 'cab-browser-status';
        status.setAttribute('role', 'status');
        status.setAttribute('aria-live', 'polite');
        status.textContent = results.length
          ? this.interpolate(this.config.strings.showing, { shown: visibleCount, total: results.length })
          : this.config.strings.noResults;
        browser.append(status);

        if (!results.length) return;

        const grid = document.createElement('ul');
        grid.className = 'cab-product-grid grid grid--2-col-tablet-down grid--4-col-desktop';
        grid.setAttribute('role', 'list');
        results.slice(0, visibleCount).forEach((product) => grid.append(this.createProductCard(slot, product)));
        browser.append(grid);

        if (visibleCount < results.length) {
          const moreWrap = document.createElement('div');
          moreWrap.className = 'cab-show-more';
          const more = document.createElement('button');
          more.type = 'button';
          more.className = 'button button--secondary';
          more.dataset.showMore = slot;
          more.textContent = this.config.strings.showMore;
          moreWrap.append(more);
          browser.append(moreWrap);
        }
      }

      isColorSelectableVariant(variant, activeColor = '') {
        if (!variant) return false;
        if (activeColor && this.normalize(variant.color) !== activeColor) return false;
        return (
          variant.available !== false &&
          Number(variant.inventoryQuantity) > 0 &&
          variant.hasOwnImage === true &&
          Boolean(variant.image?.src)
        );
      }

      getResults(slot) {
        const collectionKey = this.collectionKeyForSlot(slot);
        const products = this.products.get(collectionKey) || [];
        const query = this.normalize(this.queries[slot]);
        const terms = query.split(/\s+/).filter(Boolean);

        const activeColor = this.normalize(this.activeColors[slot]);
        return products
          .filter((product) => this.matchesSearch(product, terms))
          .filter((product) => slot !== 'head' || this.matchesFilters(product))
          .map((product) => {
            if (!activeColor) return product;
            const variants = product.variants.filter((variant) =>
              this.isColorSelectableVariant(variant, activeColor)
            );
            return variants.length ? { ...product, variants } : null;
          })
          .filter(Boolean)
          .map((product) => ({ product, score: terms.length ? this.searchScore(product, query, terms) : 0 }))
          .sort((a, b) => b.score - a.score || a.product._collectionOrder - b.product._collectionOrder)
          .map((result) => result.product);
      }

      renderColorFilter(slot) {
        const container = this.panels.get(slot)?.querySelector(`[data-color-filter="${slot}"]`);
        if (!container) return;
        const collectionKey = this.collectionKeyForSlot(slot);
        const products = this.products.get(collectionKey) || [];
        const colors = Array.from(
          new Set(products.flatMap((product) => this.colorValues(product)).filter(Boolean))
        ).sort((a, b) => a.localeCompare(b, this.config.locale, { sensitivity: 'base' }));
        container.replaceChildren();
        if (!colors.length) return;

        const details = document.createElement('details');
        details.className = 'cab-filter cab-filter--important cab-color-filter';
        const title = this.colorLabel();
        const summary = document.createElement('summary');
        summary.className = 'cab-filter__summary';
        const label = document.createElement('span');
        label.textContent = title;
        const activeCount = document.createElement('span');
        activeCount.className = 'cab-filter__active-count';
        activeCount.textContent = this.activeColors[slot] ? '1' : '';
        summary.append(label, activeCount);

        const choices = document.createElement('div');
        choices.className = 'cab-filter__choices';
        const allChoice = document.createElement('label');
        allChoice.className = 'cab-filter__choice';
        const allInput = document.createElement('input');
        allInput.type = 'radio';
        allInput.name = `CabColor-${this.dataset.sectionId}-${slot}`;
        allInput.value = '';
        allInput.checked = !this.activeColors[slot];
        allInput.dataset.colorFilterInput = slot;
        const allLabel = document.createElement('span');
        allLabel.textContent = '—';
        allChoice.append(allInput, allLabel);
        choices.append(allChoice);

        colors.forEach((color, index) => {
          const choice = document.createElement('label');
          choice.className = 'cab-filter__choice';
          const input = document.createElement('input');
          input.type = 'radio';
          input.name = `CabColor-${this.dataset.sectionId}-${slot}`;
          input.value = color;
          input.checked = this.activeColors[slot] === color;
          input.dataset.colorFilterInput = slot;
          input.id = `CabColor-${this.dataset.sectionId}-${slot}-${index}`;
          const colorLabel = document.createElement('span');
          colorLabel.textContent = color;
          choice.append(input, colorLabel);
          choices.append(choice);
        });
        details.append(summary, choices);
        container.append(details);
      }

      setColorFilter(slot, color) {
        this.activeColors[slot] = color;
        this.visibleCounts[slot] = this.config.productsPerBatch;
        this.renderSlot(slot);
        this.emitAnalytics('cab_builder_filter', { part: slot, filter_name: 'color', filter_value: color, active: Boolean(color) });
      }

      colorValues(product) {
        return product.variants
          .filter((variant) => this.isColorSelectableVariant(variant))
          .map((variant) => variant.color)
          .filter(Boolean)
          .map((value) => String(value));
      }

      colorLabel() {
        const labels = { en: 'Color', it: 'Colore', es: 'Color', nl: 'Kleur', fr: 'Couleur', de: 'Farbe', sv: 'Färg', fi: 'Väri', pl: 'Kolor', pt: 'Cor', ro: 'Culoare', ru: 'Цвет', cs: 'Barva', hu: 'Szín', ms: 'Warna', ar: 'اللون', ja: '色', ko: '색상', 'zh-CN': '颜色', 'zh-TW': '顏色' };
        return labels[this.config.locale] || labels[this.config.locale.split('-')[0]] || labels.en;
      }

      createProductCard(slot, product) {
        const item = document.createElement('li');
        item.className = 'grid__item';
        const card = document.createElement('article');
        card.className = 'cab-product-card';
        card.dataset.productId = String(product.id);

        const currentSelection = this.selections[slot];
        const selectedVariantId =
          currentSelection?.productId === product.id ? String(currentSelection.variant.id) : null;
        const initialVariant =
          product.variants.find((variant) => String(variant.id) === selectedVariantId) || product.variants[0];
        const initialImage = initialVariant.image?.src ? initialVariant.image : product.image;

        const media = document.createElement('div');
        media.className = 'cab-product-card__media';
        if (initialImage?.src) {
          const image = document.createElement('img');
          image.src = initialImage.src;
          image.alt = initialImage.alt || product.title;
          image.loading = 'lazy';
          image.width = 480;
          image.height = 480;
          media.append(image);
        }

        const body = document.createElement('div');
        body.className = 'cab-product-card__body';
        const title = document.createElement('h3');
        title.className = 'cab-product-card__title';
        title.textContent = product.title;
        body.append(title);

        if (product.variants.length > 1) {
          const variantLabel = document.createElement('label');
          variantLabel.className = 'cab-product-card__variant-label';
          variantLabel.textContent = this.config.strings.variant;
          const select = document.createElement('select');
          select.className = 'select__select cab-product-card__variant';
          select.dataset.variantSelect = slot;
          select.dataset.productId = String(product.id);
          product.variants.forEach((variant) => {
            const option = document.createElement('option');
            option.value = String(variant.id);
            option.textContent = `${variant.title} — ${variant.formattedPrice}`;
            option.selected = variant.id === initialVariant.id;
            select.append(option);
          });
          variantLabel.append(select);
          body.append(variantLabel);
        }

        const price = document.createElement('p');
        price.className = 'cab-product-card__price price';
        price.dataset.productPrice = String(product.id);
        price.textContent = initialVariant.formattedPrice;
        body.append(price);

        const selectButton = document.createElement('button');
        selectButton.type = 'button';
        selectButton.className = 'button button--secondary cab-product-card__select';
        selectButton.dataset.selectProduct = slot;
        selectButton.dataset.productId = String(product.id);
        selectButton.dataset.variantId = String(initialVariant.id);
        const isSelected = selectedVariantId === String(initialVariant.id);
        selectButton.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        selectButton.textContent = isSelected ? this.config.strings.selected : this.config.strings.select;
        card.classList.toggle('cab-product-card--selected', isSelected);

        body.append(selectButton);
        card.append(media, body);
        item.append(card);
        return item;
      }

      selectProduct(button) {
        const slot = button.dataset.selectProduct;
        const collectionKey = this.collectionKeyForSlot(slot);
        const product = (this.products.get(collectionKey) || []).find(
          (candidate) => String(candidate.id) === button.dataset.productId
        );
        if (!product) return;

        const variant = product.variants.find((candidate) => String(candidate.id) === button.dataset.variantId);
        if (!variant) return;

        this.applySelection(slot, collectionKey, product, variant);
      }

      applySelection(slot, collectionKey, product, variant, { advance = true, source = 'manual' } = {}) {
        this.selections[slot] = {
          slot,
          collectionKey,
          productId: product.id,
          handle: product.handle,
          title: product.title,
          image: variant.image?.src ? variant.image : product.image,
          variant,
        };
        this.updateSummary();
        this.renderSlot(slot);
        this.emitAnalytics('cab_builder_select_part', {
          part: slot,
          collection: this.config.collections[collectionKey],
          product: product.id,
          variant: variant.id,
          source,
        });

        const next = { hairHeadgear: 'head', head: 'torso', torso: 'legs' }[slot];
        if (advance && next) window.setTimeout(() => this.openSlot(next), 150);
      }

      handleVariantChange(select) {
        const slot = select.dataset.variantSelect;
        const collectionKey = this.collectionKeyForSlot(slot);
        const product = (this.products.get(collectionKey) || []).find(
          (candidate) => String(candidate.id) === select.dataset.productId
        );
        const variant = product?.variants.find((candidate) => String(candidate.id) === select.value);
        if (!product || !variant) return;

        const card = select.closest('.cab-product-card');
        const button = card?.querySelector('[data-select-product]');
        const price = card?.querySelector('[data-product-price]');
        const image = card?.querySelector('.cab-product-card__media img');
        const variantImage = variant.image?.src ? variant.image : product.image;
        if (button) button.dataset.variantId = String(variant.id);
        if (price) price.textContent = variant.formattedPrice;
        if (image && variantImage?.src) {
          image.src = variantImage.src;
          image.alt = variantImage.alt || product.title;
        }

        if (this.selections[slot]?.productId === product.id) {
          this.selections[slot] = {
            ...this.selections[slot],
            image: variantImage,
            variant,
          };
          this.updateSummary();
          this.renderSlot(slot);
        }
      }

      removeSelection(slot) {
        const previous = this.selections[slot];
        this.selections[slot] = null;
        this.updateSummary();
        if (this.currentSlot === slot) this.renderSlot(slot);
        if (previous) {
          this.emitAnalytics('cab_builder_remove_part', {
            part: slot,
            product: previous.productId,
            variant: previous.variant.id,
          });
        }
      }

      activateHairCategory(category) {
        if (!['hair', 'headgear'].includes(category)) return;
        this.currentHairCategory = category;
        this.querySelectorAll('[data-hair-category]').forEach((button) => {
          button.setAttribute('aria-pressed', button.dataset.hairCategory === category ? 'true' : 'false');
        });
        this.visibleCounts.hairHeadgear = this.config.productsPerBatch;
        return true;
      }

      setHairCategory(category) {
        if (this.activateHairCategory(category)) this.loadSlot('hairHeadgear');
      }

      updateSummary() {
        Object.entries(this.selections).forEach(([slot, selection]) => {
          const card = this.summaryCards.get(slot);
          if (!card) return;
          const text = card.querySelector('[data-summary-text]');
          const imageWrap = card.querySelector('[data-summary-image-wrap]');
          const image = card.querySelector('[data-summary-image]');
          const remove = card.querySelector('[data-remove-slot]');

          card.classList.toggle('cab-summary-card--selected', Boolean(selection));
          text.textContent = selection
            ? `${selection.title}${selection.variant.title === 'Default Title' ? '' : ` — ${selection.variant.title}`}`
            : this.config.strings.notSelected;
          remove.hidden = !selection;
          imageWrap.hidden = !selection?.image?.src;
          if (selection?.image?.src) {
            image.src = selection.image.src;
            image.alt = '';
          } else {
            image.removeAttribute('src');
          }
        });

        const selected = Object.values(this.selections).filter(Boolean);
        const total = selected.reduce((sum, selection) => sum + Number(selection.variant.price || 0), 0);
        this.totalElement.textContent = this.formatMoney(total);

        const canAdd = this.requiredSelectionsComplete();
        this.addButton.disabled = !canAdd;
        this.completionElement.hidden = canAdd;
        if (canAdd && !this.dataset.requiredCompleteEmitted) {
          this.dataset.requiredCompleteEmitted = 'true';
          this.emitAnalytics('cab_builder_required_complete', { build_id: this.buildId() });
        }
        if (!canAdd) delete this.dataset.requiredCompleteEmitted;
      }

      requiredSelectionsComplete() {
        const requiredSlots = this.config.requiredSlots || ['head', 'torso', 'legs'];
        return requiredSlots.every((slot) => Boolean(this.selections[slot]));
      }

      buildSearchRecord(product) {
        const searchDescriptionKey = this.config.searchDescriptionKey || 'search_description';
        const fieldText = Object.entries(product.metafields || {})
          .filter(([key]) => key !== searchDescriptionKey)
          .flatMap(([, value]) => this.toValues(value))
          .join(' ');
        const searchDescription = this.toValues(product.metafields?.[searchDescriptionKey]).join(' ');
        const skus = product.variants.map((variant) => variant.sku || '').join(' ');
        return {
          all: this.normalize(`${product.title} ${skus} ${fieldText} ${searchDescription}`),
          title: this.normalize(product.title),
          skus: this.normalize(skus),
          searchDescription: this.normalize(searchDescription),
        };
      }

      matchesSearch(product, terms) {
        return terms.every((term) => product._search.all.includes(term));
      }

      searchScore(product, query, terms) {
        let score = 0;
        if (product._search.title === query) score += 1000;
        if (product._search.skus.split(' ').includes(query)) score += 900;
        if (product._search.title.includes(query)) score += 350;
        if (product._search.searchDescription.includes(query)) score += 300;
        terms.forEach((term) => {
          if (product._search.title.includes(term)) score += 80;
          if (product._search.skus.includes(term)) score += 70;
          if (product._search.searchDescription.includes(term)) score += 45;
          this.config.fields
            .filter((field) => field.important)
            .forEach((field) => {
              if (this.fieldValues(product, field.key).some((value) => this.normalize(value).includes(term))) score += 20;
            });
        });
        return score;
      }

      renderFilterControls() {
        const products = this.products.get('head');
        if (!products) return;
        const importantContainer = this.querySelector('[data-important-filters]');
        const allContainer = this.querySelector('[data-all-filters]');
        importantContainer.replaceChildren();
        allContainer.replaceChildren();

        const filterFields = this.config.fields.filter((field) => !field.searchOnly);
        filterFields
          .filter((field) => field.important)
          .forEach((field) => importantContainer.append(this.createFilterDetails(field, products, 'important')));

        const groups = new Map();
        filterFields.forEach((field) => {
          if (!groups.has(field.group)) groups.set(field.group, []);
          groups.get(field.group).push(field);
        });
        groups.forEach((fields, groupName) => {
          const group = document.createElement('section');
          group.className = 'cab-filter-group';
          const heading = document.createElement('h3');
          heading.className = 'cab-filter-group__heading';
          heading.textContent = groupName;
          group.append(heading);
          fields.forEach((field) => group.append(this.createFilterDetails(field, products, 'all')));
          allContainer.append(group);
        });

        this.filterControlsBuilt = true;
        this.syncFilterControls();
      }

      createFilterDetails(field, products, context) {
        const values = Array.from(
          new Set(products.flatMap((product) => this.fieldValues(product, field.key)).filter(Boolean))
        ).sort((a, b) => a.localeCompare(b, this.config.locale, { sensitivity: 'base' }));
        const details = document.createElement('details');
        details.className = `cab-filter cab-filter--${context}`;
        details.dataset.filterDetails = field.key;
        if (!values.length) details.hidden = true;

        const summary = document.createElement('summary');
        summary.className = 'cab-filter__summary';
        const label = document.createElement('span');
        label.textContent = field.label;
        const activeCount = document.createElement('span');
        activeCount.className = 'cab-filter__active-count';
        activeCount.dataset.filterActiveCount = field.key;
        summary.append(label, activeCount);

        const choices = document.createElement('div');
        choices.className = 'cab-filter__choices';
        values.forEach((value, index) => {
          const choice = document.createElement('label');
          choice.className = 'cab-filter__choice';
          const input = document.createElement('input');
          input.type = 'checkbox';
          input.value = value;
          input.dataset.filterInput = 'true';
          input.dataset.filterKey = field.key;
          input.id = `CabFilter-${this.dataset.sectionId}-${context}-${field.key}-${index}`;
          const valueLabel = document.createElement('span');
          valueLabel.textContent = value;
          const count = document.createElement('span');
          count.className = 'cab-filter__count';
          count.dataset.filterCount = 'true';
          choice.append(input, valueLabel, count);
          choices.append(choice);
        });

        details.append(summary, choices);
        return details;
      }

      setFilter(key, value, checked) {
        const values = this.activeFilters.get(key) || new Set();
        if (checked) values.add(value);
        else values.delete(value);
        if (values.size) this.activeFilters.set(key, values);
        else this.activeFilters.delete(key);
        this.visibleCounts.head = this.config.productsPerBatch;
        this.renderSlot('head');
        this.syncFilterControls();
        this.emitAnalytics('cab_builder_filter', {
          part: 'head',
          filter_name: key,
          filter_value: value,
          active: checked,
        });
      }

      removeFilter(key, value) {
        this.setFilter(key, value, false);
      }

      clearFilters() {
        this.activeFilters.clear();
        this.visibleCounts.head = this.config.productsPerBatch;
        this.renderSlot('head');
        this.syncFilterControls();
      }

      syncFilterControls() {
        if (!this.filterControlsBuilt) return;
        this.querySelectorAll('[data-filter-input]').forEach((input) => {
          const selected = this.activeFilters.get(input.dataset.filterKey)?.has(input.value) || false;
          input.checked = selected;
          const count = this.countForFilterOption(input.dataset.filterKey, input.value);
          input.disabled = count === 0 && !selected;
          const countElement = input.closest('label')?.querySelector('[data-filter-count]');
          if (countElement) countElement.textContent = `(${count})`;
        });

        this.querySelectorAll('[data-filter-active-count]').forEach((element) => {
          const size = this.activeFilters.get(element.dataset.filterActiveCount)?.size || 0;
          element.textContent = size ? String(size) : '';
        });

        const clearButtons = this.querySelectorAll('[data-clear-filters]');
        clearButtons.forEach((button) => (button.hidden = this.activeFilters.size === 0));
        this.renderActiveFilters();
      }

      renderActiveFilters() {
        const container = this.querySelector('[data-active-filters]');
        container.replaceChildren();
        this.activeFilters.forEach((values, key) => {
          const field = this.config.fields.find((candidate) => candidate.key === key);
          values.forEach((value) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'cab-active-filter';
            button.dataset.removeFilter = 'true';
            button.dataset.filterKey = key;
            button.dataset.filterValue = value;
            button.textContent = `${field?.label || key}: ${value} ×`;
            container.append(button);
          });
        });
      }

      countForFilterOption(key, value) {
        const products = this.products.get('head') || [];
        const query = this.normalize(this.queries.head);
        const terms = query.split(/\s+/).filter(Boolean);
        return products.filter((product) => {
          if (!this.matchesSearch(product, terms)) return false;
          if (!this.matchesFilters(product, key)) return false;
          return this.fieldValues(product, key).includes(value);
        }).length;
      }

      matchesFilters(product, exceptKey = null) {
        for (const [key, selectedValues] of this.activeFilters.entries()) {
          if (key === exceptKey) continue;
          const productValues = this.fieldValues(product, key);
          if (![...selectedValues].some((value) => productValues.includes(value))) return false;
        }
        return true;
      }

      fieldValues(product, key) {
        return this.toValues(product.metafields?.[key]).map((value) => String(value));
      }

      toValues(value) {
        if (value === null || value === undefined || value === '') return [];
        if (Array.isArray(value)) return value.flatMap((item) => this.toValues(item));
        if (typeof value === 'object') {
          if ('label' in value) return [value.label];
          if ('value' in value) return this.toValues(value.value);
          return Object.values(value).flatMap((item) => this.toValues(item));
        }
        if (typeof value === 'string' && value.trim().startsWith('[')) {
          try {
            return this.toValues(JSON.parse(value));
          } catch (_error) {
            return [value];
          }
        }
        return [value];
      }

      async addBuildToCart() {
        if (!this.requiredSelectionsComplete()) return;
        const buildId = this.buildId();
        const items = Object.entries(this.selections)
          .filter(([, selection]) => Boolean(selection))
          .map(([slot, selection]) => ({
            id: selection.variant.id,
            quantity: 1,
            properties: {
              _cab_builder: this.config.builderType || 'minifigure',
              _cab_build_id: buildId,
              _cab_build_part: slot,
            },
          }));

        this.setAddingState(true);
        this.messageElement.textContent = '';
        this.emitAnalytics('cab_builder_add_to_cart', { build_id: buildId });

        try {
          const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          const sections = cart?.getSectionsToRender?.().map((section) => section.id) || [
            'cart-drawer',
            'cart-icon-bubble',
          ];
          cart?.setActiveElement?.(document.activeElement);

          const response = await fetch(window.routes?.cart_add_url || `${this.config.rootUrl}cart/add.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              items,
              sections,
              sections_url: window.location.pathname,
            }),
          });
          const payload = await response.json();
          if (!response.ok || payload.status) throw new Error(payload.description || payload.message || this.config.strings.cartError);

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            await publish(PUB_SUB_EVENTS.cartUpdate, {
              source: `${this.config.builderType || 'minifigure'}-builder`,
              cartData: payload,
            });
          }

          if (cart?.tagName === 'CART-NOTIFICATION' && !payload.key) {
            payload.key = payload.items?.[0]?.key;
          }

          if (cart?.renderContents && payload.sections && (cart.tagName !== 'CART-NOTIFICATION' || payload.key)) {
            cart.renderContents(payload);
          }
          else if (window.routes?.cart_url) window.location.assign(window.routes.cart_url);

          this.messageElement.textContent = this.config.strings.cartSuccess;
          this.emitAnalytics('cab_builder_cart_success', { build_id: buildId });
        } catch (error) {
          console.error(`${this.config.builderType || 'minifigure'} builder cart error`, error);
          this.messageElement.textContent = error.message || this.config.strings.cartError;
        } finally {
          this.setAddingState(false);
        }
      }

      setAddingState(adding) {
        this.addButton.classList.toggle('loading', adding);
        this.addButton.disabled = adding || !this.requiredSelectionsComplete();
        this.addLabel.textContent = adding ? this.config.strings.adding : this.config.strings.addToCart;
        this.addButton.querySelector('.loading__spinner')?.classList.toggle('hidden', !adding);
      }

      buildId() {
        const id = (slot) => this.selections[slot]?.variant.id || 0;
        const prefix = this.config.builderType === 'minidoll' ? 'md1' : 'v1';
        return `${prefix}-h${id('hairHeadgear')}-hd${id('head')}-t${id('torso')}-l${id('legs')}`;
      }

      formatMoney(amount) {
        try {
          const formatter = new Intl.NumberFormat(this.config.locale, {
            style: 'currency',
            currency: this.config.currency,
          });
          const digits = formatter.resolvedOptions().maximumFractionDigits;
          return formatter.format(Number(amount) / 10 ** digits);
        } catch (_error) {
          return String(amount);
        }
      }

      normalize(value) {
        return String(value || '')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLocaleLowerCase(this.config.locale)
          .replace(/[^\p{L}\p{N}]+/gu, ' ')
          .trim();
      }

      interpolate(template, values) {
        return Object.entries(values).reduce(
          (result, [key, value]) => result.replaceAll(`[${key}]`, String(value)),
          template
        );
      }

      getEntrySource() {
        return this.getDeepLinkRequests().length ? 'product' : 'direct';
      }

      emitAnalytics(name, detail = {}) {
        this.dispatchEvent(
          new CustomEvent('cab:analytics', {
            bubbles: true,
            detail: { event: name, ...detail },
          })
        );
      }
    }
  );
}
